/**
 * Centrální mapa veřejných rout webu — JEDINÝ zdroj pravdy pro veřejné cesty.
 *
 * Konzumenti (výhradně přes tyto buildery, žádné volné stringy):
 * - web: interní odkazy, breadcrumby, JSON-LD, canonical
 * - API: image sitemap, cache purge buildery, newsletter/agent deep-linky
 * - e-mailové šablony (packages/email-templates)
 * - admin: „Zobrazit na webu" deep-linky (`${WEB_URL}${routes.…}`)
 *
 * Úplnost vynucuje guard test (routesGuard.test.ts) — sken zdrojáků na legacy
 * literály. Redirect mapa EN → CZ se generuje ZDE (`legacyPathToCzech`), takže
 * routy a redirecty nemohou driftovat.
 *
 * SK mutace (budoucnost): výměna/rozšíření této mapy, obsahové slugy zůstávají
 * jednojazyčné.
 */

import { MONTH_SLUGS_CZ } from "./czLabels";
import type { SlugHistoryEntity } from "./slugHistory";
import type { Continent } from "./types";
import { isHotelTrip, isIsoDate } from "./utils";
import { CONTINENT_SLUGS_CZ, WEB_SECTIONS } from "./webSections";
import {
  HOME_REVIEWS_LIMIT,
  REVIEW_COLUMNS,
  REVIEWS_COLUMN_PAGE_SIZE,
  type ReviewColumn,
} from "./reviewsPublic";

/** Prefixy dynamických rout — pro parse konzumenty (tracking, agent kontext, purge prefixy). */
export const routePrefixes = {
  zajezd: "/zajezd/",
  hotel: "/hotel/",
  destinace: "/destinace/",
  pruvodce: "/pruvodce/",
  inspirace: "/inspirace/",
  blogPost: "/blog/",
  /**
   * Faseta ohlasů (řez 04, vize F4.3) — `/recenze/vietnam`. Prefix je tu proto, aby
   * `isKnownPublicPath` filtrované cesty znal; samotné `/recenze` zůstává statickou
   * cestou v `PUBLIC_STATIC_PATHS`.
   */
  recenze: "/recenze/",
  /**
   * Jednoduchá stránka (řez 12, vize F12.2) — plochá cesta `/stranka/<slug>`. Segment
   * je zároveň vyhrazený proti slugu destinace (`CONTENT_PAGE_PATH_SEGMENT` v
   * `offerUrl.ts`). Výpis `/stranka` bez slugu neexistuje.
   */
  stranka: "/stranka/",
} as const;

/** Segment cesty jednoduchých stránek — odvozený z prefixu, ne druhý literál. */
export const CONTENT_PAGE_PATH_SEGMENT = routePrefixes.stranka.replace(/\//g, "");

/**
 * Minimum, které stačí k určení kanonické cesty zájezdu. Tvarem odpovídá slim i plné
 * projekci `trips` — volající předává rovnou celý zájezd.
 */
export interface TripDetailRef {
  slug: string;
  tripType?: "guided" | "private" | "resort" | null;
  fulfillmentMode?: "fixed_departures" | "on_request" | null;
  tripKind?: "tour" | "experience" | null;
}

/**
 * Minimum, které stačí k určení kanonické adresy destinace nebo inspirace.
 *
 * `countrySlug` je slug nadřazené ZEMĚ — a „země" tu znamená zemskou ÚROVEŇ, ne
 * `parentId IS NULL`: lokalita s příznakem `showAsCountry` (Bali, Zanzibar, Hongkong,
 * Dubaj, Galapágy) vystupuje jako země, takže má vlastní plochou adresu a zároveň je
 * `countrySlug` pro své vlastní lokality. Hodnotu počítá VÝHRADNĚ API
 * (`destinationPath.ts`) a vydává ji v každé veřejné projekci destinace; web ji jen
 * předává dál, aby dvě strany nemohly dojít k jiné adrese téže stránky.
 *
 * Prázdná hodnota (`null`/`undefined`) znamená „tahle destinace JE zemská úroveň",
 * tedy plochá adresa. Není to nouzový fallback: přesně tak vypadá odkaz na zemi.
 */
export interface DestinationPathRef {
  slug: string;
  countrySlug?: string | null;
}

/**
 * Je destinace zemskou ÚROVNÍ? Země z definice, lokalita podle příznaku.
 *
 * Tohle je celá definice — nikdo ji nesmí psát znovu. Bydlí ve sdíleném balíčku, ne
 * v API, protože se na ni ptají obě strany: API z ní počítá `countrySlug` do každé
 * projekce, web z ní rozhoduje, co je země v dlaždicích `/destinace` a ve stromu „kam".
 * Dokud si ji každý odvozoval z `parentId`, měl web o dvě země míň než mega menu.
 *
 * Není to totéž jako prázdný `countrySlug`: ten nemá ani lokalita pod NEPUBLIKOVANOU
 * zemí (adresa ji vynechává, protože by ukazovala na 404), a přesto lokalitou zůstává.
 * Úroveň a adresa jsou dvě různé otázky.
 */
export function isCountryLevel(dest: {
  parentId?: string | null;
  showAsCountry?: boolean | null;
}): boolean {
  // Chybějící `parentId`/`showAsCountry` (řádek staršího tvaru, cizí volající) znamená
  // NE — `||` by jinak vrátil `undefined` a `isCountry: undefined` by v odpovědi API
  // vypadalo jako „nevíme", ne jako „není".
  return !dest.parentId || dest.showAsCountry === true;
}

/** Cesta pod prefixem: `<prefix><zeme>/<slug>` u lokality, `<prefix><slug>` u země. */
const nestedPath = (prefix: string, ref: DestinationPathRef): string =>
  ref.countrySlug && ref.countrySlug !== ref.slug
    ? `${prefix}${ref.countrySlug}/${ref.slug}`
    : `${prefix}${ref.slug}`;

/**
 * Česká kotva sekce kontinentu, nebo `null` pro hodnotu mimo enum. `hasOwnProperty`, ne
 * holý index: data z API nesou kontinent jako řetězec a `constructor` by z mapy vytáhl
 * zděděnou funkci (adresa `#function Object…`).
 */
const continentAnchor = (continent: Continent): string | null =>
  Object.prototype.hasOwnProperty.call(CONTINENT_SLUGS_CZ, continent)
    ? CONTINENT_SLUGS_CZ[continent]
    : null;

/**
 * Parametry adresy detailu zájezdu, které nese výběr termínu (řez 01, F1.3). `termin` =
 * `trip_dates.id` vybraného odjezdu; `dateFrom`/`dateTo` = období z karty zájezdu (ISO
 * `YYYY-MM-DD`).
 *
 * Hodnoty jsou WEBOVÉ (české) názvy parametrů (vize podnety-tymu-2026-10, F5.2): detail
 * čte jen skript prohlížeče, a to přímo podle téhle mapy, bez slovníku. Literál, ne
 * `webParamName`: `webParams.ts` importuje tenhle modul a opačná hrana by založila cyklus
 * s konstantami na úrovni modulu (TDZ). Slovník plochy detailu zájezdu bere názvy odsud
 * a `webParams.test.ts` hlídá, že se shodují s webovými názvy výpisu (`od`, `do`).
 */
export const TRIP_DETAIL_QUERY = {
  departure: "termin",
  dateFrom: "od",
  dateTo: "do",
} as const;
/** Parametry detailu zájezdu, které čte JEN skript v prohlížeči; server je nečte a nemění podle nich HTML. */
export const TRIP_DETAIL_CLIENT_ONLY_PARAMS: readonly string[] = Object.values(TRIP_DETAIL_QUERY);
/** Tvar id odjezdu v parametru `termin` (dnes `date_` + 8 znaků). */
export const DEPARTURE_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
/** Výběr, který si detail zájezdu přebírá z odkazu: konkrétní odjezd, nebo období. */
export type TripDetailSelection = { departureId: string } | { dateFrom: string; dateTo?: string };

/**
 * Query string výběru termínu včetně „?", nebo "" když z výběru nic platného nezbude.
 * Neplatné hodnoty vynechá (odkaz nesmí nést parametr, který detail stejně zahodí);
 * konec období bez platného začátku ani konec před začátkem nemá smysl.
 */
function tripDetailQuery(selection: TripDetailSelection | undefined): string {
  if (!selection) return "";
  const params = new URLSearchParams();
  if ("departureId" in selection) {
    if (DEPARTURE_ID_PATTERN.test(selection.departureId)) {
      params.set(TRIP_DETAIL_QUERY.departure, selection.departureId);
    }
  } else if (isIsoDate(selection.dateFrom)) {
    params.set(TRIP_DETAIL_QUERY.dateFrom, selection.dateFrom);
    if (selection.dateTo && isIsoDate(selection.dateTo) && selection.dateTo >= selection.dateFrom) {
      params.set(TRIP_DETAIL_QUERY.dateTo, selection.dateTo);
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

// Deklarované mimo `routes`, aby na ně mohl sáhnout i `tripDetail` (objektový
// literál nesmí odkazovat sám na sebe — implicitní `any` v inicializaci).
const zajezdPath = (slug: string): string => `${routePrefixes.zajezd}${slug}`;
const hotelPath = (slug: string): string => `${routePrefixes.hotel}${slug}`;
/** Kořen katalogu — sdílí ho `zajezdy()` i nabídkové adresy pod ním. */
const ZAJEZDY_PATH = "/zajezdy";

/**
 * Slug typu zájezdu v nabídkové cestě (vize B1) — je to VEŘEJNÁ adresa, takže bydlí
 * v route mapě, ne u kontraktu filtrů. Klíče jsou hodnoty `TripType`; typ se sem
 * NEimportuje (`types/*` už importuje `routes`, takže by vznikl cyklus modulů) —
 * úplnost místo toho vynucuje `offerUrl.ts`, kde se mapa přiřazuje do
 * `Record<TripType, string>` a chybějící klíč shodí typecheck.
 */
export const OFFER_TYPE_PATH_SLUGS = {
  guided: "s-pruvodcem",
  private: "na-miru",
  resort: "pobytove",
} as const;

const OFFER_TYPE_SLUGS: ReadonlySet<string> = new Set(Object.values(OFFER_TYPE_PATH_SLUGS));

/**
 * Tvar slugu v CESTĚ — týž, jaký na destinaci vynucuje `destinationCreateSchema.slug`.
 *
 * Bydlí tady, protože ho potřebují oba konzumenti tvaru nabídkové adresy: `offerUrl.ts`
 * (kde je pojistkou na výstupu, viz jeho docblock) a `parseOfferSegments` níž. Dokud ho
 * měl jen `offerUrl.ts`, přijímal `isKnownPublicPath` pod `/zajezdy/` libovolný řetězec
 * bez lomítka — takže promo lišta s odkazem `/zajezdy/Vietnam` prošla uložením a z každé
 * stránky webu vedla na tvrdou 404 (na `main` byla táž hodnota odmítnuta).
 */
export const PATH_SAFE_SLUG = /^[a-z0-9-]+$/;

/** Co z nabídkové cesty vyčetly segmenty; `null` = tenhle tvar neexistuje. */
export interface OfferSegments {
  destination: string | null;
  typeSlug: string | null;
}

/**
 * TVAR nabídkové adresy — jediné pravidlo, které říká, co je za `/zajezdy` platné.
 *
 * Bydlí v route mapě (a ne u kontraktu nabídkových adres), protože ho potřebují dva
 * konzumenti na opačných stranách: `parseOfferPath` v `offerUrl.ts`, který segmenty
 * překládá na `TripType`, a `isKnownPublicPath` o pár řádků níž, který validuje
 * admin-editovatelné odkazy a do `offerUrl.ts` sáhnout nemůže (cyklus modulů).
 * Dvě kopie pravidla by znamenaly, že jedna z nich časem pustí tvar, který druhá 404uje.
 *
 * Pravidla: prázdné segmenty se zahazují (koncové lomítko), 0 = holý výpis,
 * 1 = typ, pokud je to slug typu, jinak destinace, 2 = destinace + typ (první segment
 * NESMÍ být slug typu, druhý jím MUSÍ být), 3 a víc neexistuje. Segment destinace musí
 * mít SLUGOVÝ TVAR (`PATH_SAFE_SLUG`) — bez toho byl „platný tvar adresy" cokoli bez
 * lomítka, včetně `Vietnam`, `../x` nebo prázdného řetězce po trimu.
 */
export function parseOfferSegments(segments: readonly string[]): OfferSegments | null {
  const parts = segments.filter((segment) => segment.length > 0);

  if (parts.length === 0) return { destination: null, typeSlug: null };

  if (parts.length === 1) {
    if (OFFER_TYPE_SLUGS.has(parts[0])) return { destination: null, typeSlug: parts[0] };
    return PATH_SAFE_SLUG.test(parts[0]) ? { destination: parts[0], typeSlug: null } : null;
  }

  if (parts.length === 2) {
    if (OFFER_TYPE_SLUGS.has(parts[0]) || !PATH_SAFE_SLUG.test(parts[0])) return null;
    return OFFER_TYPE_SLUGS.has(parts[1]) ? { destination: parts[0], typeSlug: parts[1] } : null;
  }

  return null;
}

export const routes = {
  home: () => "/",

  // Zájezdy
  zajezdy: () => ZAJEZDY_PATH,
  /**
   * Nabídkové adresy katalogu (vize struktura-webu B1): `/zajezdy/<destinace>`,
   * `/zajezdy/<typ>`, `/zajezdy/<destinace>/<typ>`.
   *
   * Bere HOTOVÝ slug typu, ne enum hodnotu — mapování `TripType → slug` je součást
   * kontraktu nabídkových adres (`offerUrl.ts`), ne route mapy. Volá to výhradně
   * `offerPath`; nic jiného tuhle cestu neskládá (guard `routesGuard.test.ts`).
   */
  zajezdyNabidka: (
    segments: { destination?: string | null; typeSlug?: string | null } = {},
  ): string => {
    const parts = [segments.destination, segments.typeSlug].filter(
      (segment): segment is string => !!segment,
    );
    return parts.length > 0 ? `${ZAJEZDY_PATH}/${parts.join("/")}` : ZAJEZDY_PATH;
  },
  /**
   * Detail NEhotelového zájezdu. Znáš-li typ zájezdu, volej `tripDetail` — tenhle
   * builder je pro konzumenty, kteří mají jen slug (e-maily, newsletter, historie
   * v localStorage); hotel na něm skončí a stránka udělá 301 na `/hotel/[slug]`.
   */
  zajezd: zajezdPath,

  /**
   * Kanál „na míru" (řez 07, vize F1/B7) — stránka s vysvětlením produktu a poptávkou
   * BEZ konkrétního zájezdu. Stojí MIMO jmenný prostor `/zajezdy`, aby nekolidovala
   * s typovým výpisem `/zajezdy/na-miru` (publikované zájezdy typu `private`): to je
   * nabídka z katalogu, tohle je poptávka, kterou katalog nepokrývá.
   */
  zajezdNaMiru: () => "/zajezd-na-miru",

  // Hotely (pobytový zájezd na vyžádání — vize D2/D5)
  hotely: () => "/hotely",
  /** Kanonický detail hotelu; nehotelový zájezd tu dostane 301 na `/zajezd/[slug]`. */
  hotel: hotelPath,
  /**
   * JEDINÝ builder pro odkazy na detail zájezdu, které typ znají — vrací kanonickou
   * cestu, takže odkaz nejde přes 301. Hotel = `resort` + rezervace na vyžádání
   * (`isHotelTrip`), všechno ostatní je běžný zájezd.
   *
   * `selection` (řez 01) přidá parametry výběru termínu (`TRIP_DETAIL_QUERY`) — jen
   * u NEhotelového zájezdu: hotel má vlastní widget a období z karty nepřebírá.
   */
  tripDetail: (trip: TripDetailRef, selection?: TripDetailSelection) =>
    isHotelTrip(trip)
      ? hotelPath(trip.slug)
      : `${zajezdPath(trip.slug)}${tripDetailQuery(selection)}`,

  /**
   * Destinace — bez argumentu výpis zemí, s odkazem detail. Země má plochou adresu
   * (`/destinace/vietnam`), lokalita vnořenou pod svou zemí (`/destinace/vietnam/hoi-an`);
   * rozhoduje o tom `countrySlug` v odkazu, ne volající.
   *
   * Bere ODKAZ, ne holý slug, právě proto: dokud to byl string, neexistovalo místo,
   * kde by se dala vnořená adresa složit, a každý volající by si musel pamatovat, že
   * u lokality patří před slug ještě země. Typ to teď vynutí — chybějící `countrySlug`
   * je tvrzení „tohle je země", ne opomenutí.
   */
  destinace: (ref?: DestinationPathRef) =>
    ref ? nestedPath(routePrefixes.destinace, ref) : "/destinace",
  /**
   * Sekce kontinentu na výpisu destinací (patička, drobečky, JSON-LD). Kotva je česká
   * z mapy kotev (`CONTINENT_SLUGS_CZ`, F5.1); vstup je kontinent enumu, ne kotva.
   */
  destinaceKontinent: (continent: Continent) => {
    const anchor = continentAnchor(continent);
    return anchor ? `/destinace#${anchor}` : "/destinace";
  },
  /**
   * Tým destinace. Zůstává na PLOCHÉ adrese (`/destinace/<slug>/tym`), i když detail
   * destinace se u lokality vnořuje — je to podstránka jedné destinace, ne adresa
   * v atlasu, a vnořený tvar by znamenal druhou kopii celé stránky kvůli případu, který
   * v katalogu neexistuje (týmy má pět zemí, žádná lokalita).
   *
   * Bere přesto ODKAZ, ne slug: kdyby tým jednou lokalita dostala, je tohle jediné místo,
   * kde se tvar změní — volající se přepisovat nebudou.
   */
  destinaceTym: (ref: DestinationPathRef) => `${routePrefixes.destinace}${ref.slug}/tym`,

  /**
   * Inspirace (aktivity) — bez argumentu výpis, s odkazem detail vnořený pod zemí
   * (`/inspirace/vietnam/hoi-an-cooking-class`). Inspirace bez destinace neexistuje
   * (vazba je povinná), takže prázdný `countrySlug` je tu degradace pro data staršího
   * tvaru: vrátí plochou adresu, kterou routa 301 přesměruje na kanonickou.
   */
  inspirace: (ref?: DestinationPathRef) =>
    ref ? nestedPath(routePrefixes.inspirace, ref) : "/inspirace",

  /** Podcasty (řez 10) — interní výpis epizod; bez publikované epizody vrací 404. */
  podcasty: () => "/podcasty",
  /**
   * Video inspirace (řez 11) — reels na výšku členěné po kontinentech. Na rozdíl
   * od `/podcasty` NENÍ gatovaná na datech (položka menu na ni vede vždy), takže
   * bez videí vrací 200 s prázdným stavem, ne 404. `continent` = kontinent enumu, jehož
   * sekce je cílem (odkaz „Všechna videa“ ze stránky země nad stropem karuselu, vize F8.7);
   * kotva je česká z `CONTINENT_SLUGS_CZ`, bez kontinentu adresa bez kotvy.
   */
  videoInspirace: (continent?: Continent | null) => {
    const anchor = continent ? continentAnchor(continent) : null;
    return anchor ? `/video-inspirace#${anchor}` : "/video-inspirace";
  },

  // Průvodci
  pruvodci: () => "/pruvodci",
  pruvodce: (slug: string) => `${routePrefixes.pruvodce}${slug}`,

  // Obsahové stránky
  recenze: () => "/recenze",
  /**
   * Recenze zúžené na destinaci a její publikovaný podstrom (řez 03, vize F3.3). Faseta
   * je PLOCHÁ pro zemi i místo (`recenze/vietnam`, `recenze/hoi-an`): slugy destinací
   * jsou globálně unikátní, takže poslední segment destinaci určí sám. Proto tu není
   * `DestinationPathRef` jako u `routes.destinace()`: faseta se nikdy nevnořuje.
   */
  recenzeDestinace: (slug: string) => `${routePrefixes.recenze}${slug}`,
  /**
   * HTML fragment karet jedné strany sloupce `/recenze` (řez 03, vize F3.2) — dotahuje
   * ho skript „Zobrazit další". NENÍ to stránka: `noindex`, mimo sitemapu
   * (`WEB_NON_PAGE_PATHS`) i mimo `PUBLIC_STATIC_PATHS`. Parametry skládá jen
   * `reviewCardsFragmentHref`.
   */
  recenzeKarty: () => "/recenze-karty",
  /**
   * HTML fragment karet 7. až posledního zájezdu jedné skupiny sekce zájezdů detailu
   * inspirace (řez 06, vize F6.2) — dotahuje ho skript „Zobrazit další". NENÍ to stránka:
   * `noindex`, `Cache-Control: no-store`, mimo sitemapu (`WEB_NON_PAGE_PATHS`) i mimo
   * `PUBLIC_STATIC_PATHS`. Parametry skládá jen `inspirationTripsFragmentHref`.
   */
  inspiraceZajezdyKarty: () => "/inspirace-zajezdy",
  oNas: () => "/o-nas",
  kontakt: () => "/kontakt",
  casteDotazy: () => "/caste-dotazy",
  darkovePoukazy: () => "/darkove-poukazy",
  cestovniAkademie: () => "/cestovni-akademie",
  kviz: () => "/kviz",
  /** Hledač inspirací — druhá varianta průvodce cestou (`tripWizard.ts`), výsledkem jsou země. */
  hledacInspiraci: () => "/hledac-inspiraci",
  pojisteni: () => "/pojisteni",
  obchodniPodminky: () => "/obchodni-podminky",
  ochranaOsobnichUdaju: () => "/ochrana-osobnich-udaju",
  kdyVyrazit: () => "/kdy-vyrazit",
  /**
   * Informační stránka jednoho měsíce (řez 8) — kam v něm vyrazit, jaké je počasí,
   * jaké svátky v něm padnou. Do prodeje odvádí až odkazem dole na stránce, takže
   * `/kdy-vyrazit/leden` NENÍ synonymum zaniklého měsíčního filtru výpisu.
   */
  kdyVyrazitMesic: (monthSlug: string) => `/kdy-vyrazit/${monthSlug}`,
  charita: () => "/charita",
  historie: () => "/historie",

  // Beze změny (E2)
  blog: () => "/blog",
  blogPost: (slug: string) => `${routePrefixes.blogPost}${slug}`,
  /** Jednoduchá stránka (řez 12) — plochá cesta, koncept je 404. */
  stranka: (slug: string) => `${routePrefixes.stranka}${slug}`,
  newsletter: () => "/newsletter",
  newsletterPreferences: () => "/newsletter/preferences",

  // Tokenové routy (noindex, beze změny)
  portal: (token: string) => `/portal/${token}`,
  offer: (token: string) => `/offer/${token}`,
  sign: (token: string) => `/sign/${token}`,
} as const;

/**
 * Dotazovací adresa ohlasů — JEDINÉ místo, kde vzniká (řez 04, vize F4.10/F4.11).
 *
 * Do řezu 04 byl ten literál opsaný na pěti místech webu a ve čtyřech variantách
 * v `purgeReviews`; `TripDetail.astro` o tom nesl komentář „URL musí zůstat doslova
 * shodná s variantou v purgeReviews", což je přesně ten drift, který se tímhle zavírá:
 * mazání mezipaměti je EXAKTNÍ podle adresy, takže jediný znak navíc znamená stránku,
 * která se po uložení ohlasu neobnoví.
 *
 * `limit` se píše VÝSLOVNĚ (i když se rovná výchozí hodnotě routy): purge i stránka si
 * tak skládají týž řetězec bez ohledu na to, jakou výchozí hodnotu má zrovna API.
 * `page` se u první stránky vynechává — první stránka je kanonicky adresa bez něj.
 */
export interface ReviewsApiQuery {
  /**
   * Slug publikované destinace (země i místo); výpis zahrne její publikovaný podstrom.
   * Neznámá hodnota končí na API 404, ne prázdnem.
   */
  destination?: string | null;
  page?: number | null;
  limit?: number | null;
  featured?: boolean;
  trip?: string | null;
  guide?: string | null;
}

/**
 * Základ dotazovací adresy ohlasů. Je vytažený, protože ho čte i REGISTRACE routy
 * v API: podcesta facetového seznamu se z něj odvozuje, ať se adresa, na kterou se
 * ptá web, a cesta, na kterou routa odpovídá, nemůžou rozejít.
 */
export const REVIEWS_API_BASE = "/api/reviews";

export function reviewsApiPath(query: ReviewsApiQuery = {}): string {
  const params = new URLSearchParams();
  if (query.featured) params.set("featured", "true");
  if (query.trip) params.set("trip", query.trip);
  if (query.guide) params.set("guide", query.guide);
  if (query.destination) params.set("destination", query.destination);
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.limit) params.set("limit", String(query.limit));
  const search = params.toString();
  return search ? `${REVIEWS_API_BASE}?${search}` : REVIEWS_API_BASE;
}

/** Facetový seznam ohlasů — destinace, které aspoň jeden schválený ohlas mají. */
export function reviewFacetsApiPath(): string {
  return `${REVIEWS_API_BASE}${REVIEW_FACETS_SUBPATH}`;
}

/**
 * Podcesta facetového seznamu uvnitř routy ohlasů — tím, čím ji registruje Hono.
 * Samostatná konstanta proto, že literál `"/destinations"` je legacy veřejný atom
 * (`routesGuard`) a registrace routy si ho psát nesmí.
 */
export const REVIEW_FACETS_SUBPATH = "/destinations";

/** Zaniklý parametr strany `/recenze`; stránka ho nečte, `webParams.ts` ho zahodí jako neznámý (F5.2). */
export const REVIEWS_LEGACY_PAGE_PARAM = "page";

/** Parametr jednoduchého filtru bez JavaScriptu; stránka ho přesměruje na fasetu. */
export const REVIEWS_DESTINATION_QUERY_PARAM = "destinace";

/** Strana sloupce jako parametr adresy: jen celé číslo > 1, strana 1 parametr nemá. */
const columnPageParam = (page: number | null | undefined): string | null =>
  typeof page === "number" && Number.isInteger(page) && page > 1 ? String(page) : null;

/** Adresa stránky `/recenze` — faseta a strany obou sloupců. */
export interface ReviewsPageQuery {
  destinationSlug?: string | null;
  klienti?: number | null;
  google?: number | null;
}

/**
 * Kanonická adresa stránky recenzí (řez 03, vize F3.2): cesta fasety a parametry stran
 * sloupců > 1 v pořadí `klienti`, `google`. Strana ≤ 1 nebo neplatná = parametr chybí.
 * JEDINÉ místo, kde adresa vzniká — `<link rel="canonical">`, odkaz „Zobrazit další"
 * i přesměrování starých parametrů se tak nemůžou rozejít.
 */
export function reviewsPageHref(query: ReviewsPageQuery = {}): string {
  const path = query.destinationSlug
    ? routes.recenzeDestinace(query.destinationSlug)
    : routes.recenze();
  const params = new URLSearchParams();
  for (const column of REVIEW_COLUMNS) {
    const value = columnPageParam(query[column]);
    if (value !== null) params.set(column, value);
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

/** Dotaz fragmentu karet: sloupec, faseta a strana (≥ 1). */
export interface ReviewCardsFragmentQuery {
  zdroj: ReviewColumn;
  destinationSlug?: string | null;
  strana: number;
}

/** Adresa HTML fragmentu karet jedné strany sloupce (parametry `zdroj`, `destinace`, `strana`). */
export function reviewCardsFragmentHref(query: ReviewCardsFragmentQuery): string {
  const params = new URLSearchParams();
  params.set("zdroj", query.zdroj);
  if (query.destinationSlug) params.set(REVIEWS_DESTINATION_QUERY_PARAM, query.destinationSlug);
  params.set("strana", String(query.strana));
  return `${routes.recenzeKarty()}?${params.toString()}`;
}

/**
 * Parametr detailu inspirace, kterým se rozbalí skupina sekce zájezdů (řez 06, vize F6.2).
 * Opakovatelný (`?zajezdy=obsahuji&zajezdy=dalsi`), neznámá hodnota se ignoruje. Server ho
 * čte, proto je v `SERVER_QUERY_PARAMS` (klíč keše HTML) pod `routes.inspirace()`.
 */
export const INSPIRATION_TRIPS_EXPAND_PARAM = "zajezdy";

/**
 * Skupiny sekce zájezdů detailu inspirace v pořadí na stránce: `obsahuji` = zájezdy
 * s vazbou na inspiraci („Zájezdy, na kterých to zažijete"), `dalsi` = ostatní zájezdy
 * destinace inspirace. Hodnoty jsou zároveň hodnoty `zajezdy` a `skupina` v adresách.
 */
export const INSPIRATION_TRIP_GROUPS = ["obsahuji", "dalsi"] as const;
export type InspirationTripGroup = (typeof INSPIRATION_TRIP_GROUPS)[number];

/** Dotaz fragmentu karet zájezdů inspirace: slug inspirace a skupina. */
export interface InspirationTripsFragmentQuery {
  inspirationSlug: string;
  group: InspirationTripGroup;
}

/** Adresa HTML fragmentu karet jedné skupiny (parametry `inspirace`, `skupina`). */
export function inspirationTripsFragmentHref(query: InspirationTripsFragmentQuery): string {
  const params = new URLSearchParams();
  params.set("inspirace", query.inspirationSlug);
  params.set("skupina", query.group);
  return `${routes.inspiraceZajezdyKarty()}?${params.toString()}`;
}

/**
 * Odkaz „Zobrazit další" skupiny bez JavaScriptu: tatáž stránka detailu inspirace
 * s `group` přidanou k již rozbaleným skupinám (`expanded`), bez duplicit, v pořadí
 * `INSPIRATION_TRIP_GROUPS` (jedna adresa na množinu rozbalených skupin, tedy i jeden
 * záznam keše HTML), s kotvou `#zajezdy-<group>`.
 */
export function inspirationExpandedHref(
  ref: DestinationPathRef,
  expanded: readonly InspirationTripGroup[],
  group: InspirationTripGroup,
): string {
  const wanted = new Set<InspirationTripGroup>([...expanded, group]);
  const params = new URLSearchParams();
  for (const g of INSPIRATION_TRIP_GROUPS) {
    if (wanted.has(g)) params.append(INSPIRATION_TRIPS_EXPAND_PARAM, g);
  }
  // Parametr `zajezdy` je na detailu inspirace webovým názvem sám o sobě (identita
  // slovníku, `webParams.test.ts`), takže se výstup přes `internalParamsToWeb` nepřekládá;
  // `webParams.ts` sem importovat nejde (cyklus modulů).
  return `${routes.inspirace(ref)}?${params.toString()}#${WEB_SECTIONS.inspiration.tripsGroupPrefix}${group}`;
}

/** Základ veřejného vyhledání historie slugů (registrace routy v API ho čte taky). */
export const SLUG_REDIRECTS_API_BASE = "/api/slug-redirects";

/**
 * Adresa vyhledání historie slugů (vize podnety-tymu-2026-10, F5.4): dnešní slug
 * publikované entity typu `entity`, která kdysi nesla `slug`. Slug kóduje funkce sama,
 * volající předává SYROVÝ slug (jinak dvojí kódování).
 */
export function slugRedirectApiPath(entity: SlugHistoryEntity, slug: string): string {
  return `${SLUG_REDIRECTS_API_BASE}/${entity}/${encodeURIComponent(slug)}`;
}

/**
 * Základ veřejného výpisu Google recenzí. Vytažený ze stejného důvodu jako
 * `REVIEWS_API_BASE`: čte ho i registrace routy v API.
 */
export const GOOGLE_REVIEWS_API_BASE = "/api/google-reviews";

export interface GoogleReviewsApiQuery {
  /** Slug publikované destinace; výpis zahrne celý její publikovaný podstrom. */
  destination?: string | null;
  page?: number | null;
  limit?: number | null;
}

/**
 * Dotazovací adresa veřejného výpisu Google recenzí — týž tvar jako `reviewsApiPath`
 * (`limit` výslovně, `page` u první strany vynechaný), protože promazání keše je
 * exaktní podle adresy.
 */
export function googleReviewsApiPath(query: GoogleReviewsApiQuery = {}): string {
  const params = new URLSearchParams();
  if (query.destination) params.set("destination", query.destination);
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.limit) params.set("limit", String(query.limit));
  const search = params.toString();
  return search ? `${GOOGLE_REVIEWS_API_BASE}?${search}` : GOOGLE_REVIEWS_API_BASE;
}

/**
 * Dotaz jedné strany sloupce `/recenze` — JEDINÝ stavitel: čte ho web (sloupce
 * i fragment) a plánuje ho promazání po uložení recenze. Vždy s `limit`
 * `REVIEWS_COLUMN_PAGE_SIZE`.
 */
export function reviewColumnApiPath(query: {
  zdroj: ReviewColumn;
  destination?: string | null;
  page?: number | null;
}): string {
  const shared = {
    destination: query.destination,
    page: query.page,
    limit: REVIEWS_COLUMN_PAGE_SIZE,
  };
  return query.zdroj === "klienti" ? reviewsApiPath(shared) : googleReviewsApiPath(shared);
}

/**
 * Dotazy bloku recenzí na homepage (vize F3.8): doporučené a nejnovější jako doplnění.
 * JEDINÝ zdroj — homepage je čte, promazání je plánuje.
 */
export function homeReviewsApiPaths(): { featured: string; newest: string } {
  return {
    featured: reviewsApiPath({ featured: true, limit: HOME_REVIEWS_LIMIT }),
    newest: reviewColumnApiPath({ zdroj: "klienti" }),
  };
}

/**
 * Cesty sitemap — adresy, které by se jinak opisovaly na čtyřech místech naráz
 * (generovaný `robots.txt`, nadřazený index webu, self-URL částí v API routě
 * a registrace API rout). Nejsou to stránky, takže do `routes`/`PUBLIC_STATIC_PATHS`
 * nepatří, ale platí pro ně totéž pravidlo: přejmenovat se smí právě tady, jinak by
 * `robots.txt` inzeroval `Sitemap:` na 404 a nikdo by si toho nevšiml.
 *
 * Buildery vracejí jen CESTU, stejně jako `routes.*` — origin doplňuje volající
 * (`site.origin` webu, `apiUrl(c.env)` workeru), protože web a API stojí na jiných
 * hostitelích a sitemapu na cizím hostiteli autorizuje jedině přímý řádek `Sitemap:`.
 */
export const sitemapPaths = {
  /** Nadřazený index webu — vstup, který robot dostane jako první. */
  index: () => "/sitemap.xml",
  /**
   * Statická sitemapa z `@astrojs/sitemap`. Číslo části generuje integrace; s dnešními
   * ~29 staticky známými cestami a `entryLimit` 45 000 je počet částí trvale jedna.
   */
  staticPart: () => "/sitemap-0.xml",
  /** Obsahová sitemapa na API; s `part` N-tá část nad stropem `SITEMAP_PART_LIMIT`. */
  apiContent: (part?: number): string =>
    part === undefined ? "/api/sitemap.xml" : `/api/sitemap.xml?part=${part}`,
  /** Obrázková sitemapa na API (media pipeline). */
  apiImages: () => "/api/sitemap-images.xml",
} as const;

/**
 * Prefixy tokenových rout. Jejich HTML je per-zákazník (jméno, částky, stav podpisu),
 * takže se NESMÍ dostat do sdílené edge cache — middleware webu podle tohohle seznamu
 * vynechá `s-maxage` hlavičku a pošle `private, no-store`.
 */
export const TOKEN_ROUTE_PREFIXES: readonly string[] = [
  routes.portal(""),
  routes.offer(""),
  routes.sign(""),
];

/**
 * Všechny statické veřejné cesty — pro validaci interních odkazů (PromoBar href) a pro
 * cache purge změn, které se propisují do SSR hlavičky/patičky KAŽDÉ stránky (řez 10:
 * položka „Podcasty" v menu se objevuje a mizí podle počtu publikovaných epizod).
 */
export const PUBLIC_STATIC_PATHS: readonly string[] = [
  routes.home(),
  routes.zajezdy(),
  // Čisté typové výpisy (`/zajezdy/s-pruvodcem`…) jsou statické veřejné cesty jako
  // kterákoli jiná — validace admin-editovatelných odkazů i purge hubů je musí znát.
  ...Object.values(OFFER_TYPE_PATH_SLUGS).map((typeSlug) => routes.zajezdyNabidka({ typeSlug })),
  routes.zajezdNaMiru(),
  routes.hotely(),
  routes.destinace(),
  routes.inspirace(),
  routes.podcasty(),
  routes.videoInspirace(),
  routes.pruvodci(),
  routes.recenze(),
  routes.oNas(),
  routes.kontakt(),
  routes.casteDotazy(),
  routes.darkovePoukazy(),
  routes.cestovniAkademie(),
  routes.kviz(),
  routes.hledacInspiraci(),
  routes.pojisteni(),
  routes.obchodniPodminky(),
  routes.ochranaOsobnichUdaju(),
  routes.kdyVyrazit(),
  // Všech dvanáct měsíčních stránek (řez 8): jsou to plnohodnotné statické cesty,
  // i když je obsluhuje jedna routa s parametrem. Kvůli tomu je `@astrojs/sitemap`
  // sám nevyjmenuje — `apps/web/astro.config.mjs` proto krmí `customPages` PRÁVĚ
  // tímhle seznamem. Dál je potřebuje `isKnownPublicPath` (validace odkazů
  // z administrace) a purge svátků na ně musí umět mířit.
  ...MONTH_SLUGS_CZ.map((slug) => routes.kdyVyrazitMesic(slug)),
  routes.charita(),
  routes.historie(),
  routes.blog(),
  routes.newsletter(),
  routes.newsletterPreferences(),
];

/**
 * Veřejné cesty, které do SITEMAPY nepatří, ačkoli existují a jsou dostupné.
 *
 * Vlastní seznam, protože „je to veřejná stránka" a „nabízíme ji robotovi" jsou dvě
 * různé věci a každou z nich potřebuje někdo jiný: `PUBLIC_STATIC_PATHS` slouží purge
 * a validaci odkazů z administrace, tenhle výčet sitemapám. Bydlí tady, aby se statická
 * sitemapa webu (`apps/web/astro.config.mjs`) a obsahová sitemapa API
 * (`routes/public/sitemap.ts`) nemohly rozejít — dokud to byl literál na obou stranách,
 * dostal se `zajezdy/pobytove` do jedné a do druhé ne.
 *
 * Důvody, každý jiný:
 *  - `zajezdy/pobytove` se KANONIZUJE na `/hotely` (vize B3), takže by sitemapa robotovi
 *    nabízela adresu, o které mu zároveň říká, ať ji nezapisuje,
 *  - `/podcasty` bez publikované epizody vrací skutečnou 404,
 *  - `/newsletter/preferences` je osobní stránka za tokenem z e-mailu.
 */
export const SITEMAP_EXCLUDED_PATHS: readonly string[] = [
  routes.zajezdyNabidka({ typeSlug: OFFER_TYPE_PATH_SLUGS.resort }),
  routes.podcasty(),
  routes.newsletterPreferences(),
];

/**
 * Cesty webu, které NEJSOU stránky (HTML fragmenty pro skripty). Do sitemapy nepatří,
 * ale nejsou ani v `PUBLIC_STATIC_PATHS` (nejsou cílem odkazu ani purge hubu), takže je
 * `SITEMAP_EXCLUDED_PATHS` nést nesmí — ten vylučuje jen skutečné veřejné stránky.
 */
export const WEB_NON_PAGE_PATHS: readonly string[] = [
  routes.recenzeKarty(),
  routes.inspiraceZajezdyKarty(),
];

const segmentPrefixMatch = (path: string, prefix: string) =>
  path === prefix || path.startsWith(`${prefix}/`);

/**
 * Patří cesta do STATICKÉ sitemapy webu? Jediné pravidlo, které to rozhoduje.
 *
 * Vyloučené cesty jsou jen půlka odpovědi. Druhá je VLASTNICTVÍ: nabídkovou rodinu
 * (`/zajezdy` a všechno pod ní) vydává obsahová sitemapa API, protože jen ta zná
 * destinační nabídkové stránky z databáze. Kdyby ji vydávaly obě, robot dostane tutéž
 * stránku dvakrát — a ne ve stejném tvaru: `@astrojs/sitemap` staví adresy s KONCOVÝM
 * LOMÍTKEM (`build.format: "directory"`), kdežto nabídkové stránky si `<link rel=
 * "canonical">` skládají `canonicalOfferPath`em, tedy bez něj. Sitemapa webu tak
 * inzerovala `zajezdy/s-pruvodcem/`, zatímco stránka na té adrese o sobě říká, že
 * kanonicky bydlí na `zajezdy/s-pruvodcem` — doslova ten stav, kvůli kterému
 * `SITEMAP_EXCLUDED_PATHS` vzniklo. (Zbytek webu tenhle rozpor nemá: ostatní stránky
 * kanonickou adresu odvozují od TÉ NAVŠTÍVENÉ, takže se s tvarem v sitemapě shodnou.)
 *
 * Vlastnictvím, ne vyloučením: `SITEMAP_EXCLUDED_PATHS` znamená „robotovi to nedáváme
 * vůbec" a čtou ho OBĚ sitemapy — kdyby se sem nabídková rodina zapsala, zmizela by
 * z indexu úplně.
 */
export function isWebSitemapPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (segmentPrefixMatch(path, ZAJEZDY_PATH)) return false;
  if (WEB_NON_PAGE_PATHS.some((nonPage) => segmentPrefixMatch(path, nonPage))) return false;
  return !SITEMAP_EXCLUDED_PATHS.some((excluded) => segmentPrefixMatch(path, excluded));
}

const KNOWN_STATIC_PATHS: ReadonlySet<string> = new Set(PUBLIC_STATIC_PATHS);

/**
 * Je cesta známou veřejnou routou webu? (statická routa, nebo dynamický prefix
 * se slugem). Query string/hash a koncové lomítko se ignorují. Použití: validace
 * admin-editovatelných interních odkazů proti route mapě a všude, kde se hodnota
 * z venku ukládá nebo vykresluje jako NAŠE cesta (`originPath` poptávky na míru).
 *
 * Odpověď „ano" znamená ROOT-RELATIVNÍ cesta našeho webu — tuhle půlku kontraktu
 * si dřív dopisoval každý volající sám (`startsWith("/") && !startsWith("//")` ve
 * `customInquiry.ts`), takže druhý konzument téže hodnoty (`POST /api/inquiries/custom`)
 * odpovídal na týž vstup jinak. Pravidlo patří sem, ať obě strany dají stejnou odpověď:
 *  - absolutní adresa (`https://…`) i protokol-relativní (`//cizi.cz/zajezdy`) → `false`,
 *  - `/\cizi.cz` taky: prohlížeč zpětné lomítko normalizuje na `/`, takže je to
 *    protokol-relativní adresa napsaná jinak.
 */
export function isKnownPublicPath(path: string): boolean {
  let p = path.split(/[?#]/)[0];
  if (!p.startsWith("/") || p[1] === "/" || p[1] === "\\") return false;
  if (p.length > 1 && p.endsWith("/")) p = p.replace(/\/+$/, "");
  if (KNOWN_STATIC_PATHS.has(p)) return true;
  // Nabídkové adresy (řez 02) nejsou ani statická množina, ani prefix se slugem —
  // jsou to jeden až dva segmenty s vlastním pravidlem tvaru. Bez téhle větve by
  // admin neuložil promo lištu s odkazem `zajezdy/vietnam`, tedy přesně s tou
  // adresou, na kterou od řezu 02 vede mega menu i hero hledač, zatímco
  // `zajezdy/pobytove` (statická cesta) by prošlo.
  const offerPrefix = `${ZAJEZDY_PATH}/`;
  if (p.startsWith(offerPrefix)) {
    return parseOfferSegments(p.slice(offerPrefix.length).split("/")) !== null;
  }
  return Object.values(routePrefixes).some(
    (prefix) => p.startsWith(prefix) && p.length > prefix.length,
  );
}

// ── Legacy EN → CZ redirect mapa (generuje middleware webu + guard test) ─────────

/** Staré EN cesty → nové CZ cesty (exact match). */
export const LEGACY_STATIC_REDIRECTS: Readonly<Record<string, string>> = {
  "/trips": routes.zajezdy(),
  "/destinations": routes.destinace(),
  "/places": routes.destinace(), // holé /places redirectovalo na výpis destinací už před CZ řezem
  "/inspirations": routes.inspirace(),
  "/guides": routes.pruvodci(),
  "/reviews": routes.recenze(),
  "/about": routes.oNas(),
  "/contact": routes.kontakt(),
  "/faq": routes.casteDotazy(),
  "/gift-vouchers": routes.darkovePoukazy(),
  "/travel-academy": routes.cestovniAkademie(),
  "/quiz": routes.kviz(),
  "/insurance": routes.pojisteni(),
  "/terms": routes.obchodniPodminky(),
  "/privacy": routes.ochranaOsobnichUdaju(),
  "/best-time-to-visit": routes.kdyVyrazit(),
  "/charity": routes.charita(),
  "/timeline": routes.historie(),
};

/**
 * Vyřazené cesty webu (řez 05: `/gdpr` F17.1, užitečné rady F17.3) → jediný cíl (301).
 *
 * Klíč = vyřazená cesta; platí pro ni i pro CELÝ její podstrom a cíl je vždy jeden, bez
 * přípony zbytku cesty (`/tips/visa` → `/caste-dotazy`, ne `/caste-dotazy/visa`). Proto
 * nejde o `LEGACY_PREFIX_REDIRECTS`, který zbytek cesty připojuje. Stránka vyřazené cesty
 * neexistuje a cesta není v `PUBLIC_STATIC_PATHS`; `legacyPathToCzech` tuhle mapu
 * zkouší PRVNÍ, takže má přednost před každou jinou. Klíče jsou zároveň zakázané atomy
 * `routesGuard.test.ts`: literál vyřazené cesty se do kódu nevrátí.
 */
export const RETIRED_PATH_REDIRECTS: Readonly<Record<string, string>> = {
  "/gdpr": routes.ochranaOsobnichUdaju(),
  "/uzitecne-rady": routes.casteDotazy(),
  "/tips": routes.casteDotazy(),
};

/** Cíl `RETIRED_PATH_REDIRECTS` pro cestu (bez koncového lomítka) nebo její podstrom, jinak `null`. */
function retiredPathRedirect(path: string): string | null {
  const zasah = Object.entries(RETIRED_PATH_REDIRECTS).find(([vyrazena]) =>
    segmentPrefixMatch(path, vyrazena),
  );
  return zasah ? zasah[1] : null;
}

/**
 * Přejmenované slugy destinací: stará cesta → nová (301). Klíč je DEKÓDOVANÁ cesta —
 * `url.pathname` chodí percent-encoded (`/destinace/mauricius%20perla…`), takže
 * `legacyPathToCzech` porovnává až po `decodeURIComponent`.
 *
 * Na rozdíl od legacy EN map tady nejde o překlad jazyka, ale o sloučení dvou řádků
 * katalogu do jednoho (vize F6 — dva Mauriciové). Cíl NESMÍ být zároveň klíčem, jinak
 * by vznikla nekonečná smyčka; hlídá to `routes.test.ts`.
 */
export const RENAMED_PATH_REDIRECTS: Readonly<Record<string, string>> = {
  "/destinace/mauricius1": routes.destinace({ slug: "mauricius" }),
  "/destinace/mauricius perla indickeho oceanu": routes.destinace({ slug: "mauricius" }),
};

/** Staré EN prefixy dynamických rout → nové CZ prefixy. /places/* je sjednocené pod /destinace/*. */
export const LEGACY_PREFIX_REDIRECTS: ReadonlyArray<readonly [from: string, to: string]> = [
  ["/trip/", routePrefixes.zajezd],
  ["/destinations/", routePrefixes.destinace],
  ["/places/", routePrefixes.destinace],
  ["/guide/", routePrefixes.pruvodce],
  ["/inspirations/", routePrefixes.inspirace],
];

/**
 * Přelož legacy EN pathname na novou českou cestu; `null` = žádný redirect.
 * Toleruje koncové lomítko (`/trips/` → `/zajezdy`) — sitemapy i staré odkazy
 * lomítkové varianty obsahují. Query string řeší volající (middleware ho
 * zachovává beze změny).
 */
export function legacyPathToCzech(pathname: string): string | null {
  let p = pathname;
  if (p.length > 1 && p.endsWith("/")) p = p.replace(/\/+$/, "");

  const retired = retiredPathRedirect(p);
  if (retired) return retired;

  const exact = LEGACY_STATIC_REDIRECTS[p];
  if (exact) return exact;

  const renamed = renamedSlugRedirect(p);
  if (renamed) return renamed;

  // /destinations/[slug]/team → /destinace/[slug]/tym (překlad i posledního segmentu)
  const team = /^\/destinations\/([^/]+)\/team$/.exec(p);
  if (team) return routes.destinaceTym({ slug: team[1] });

  for (const [from, to] of LEGACY_PREFIX_REDIRECTS) {
    if (p.startsWith(from) && p.length > from.length) {
      // Mapa přejmenovaných slugů se aplikuje i na VÝSLEDEK prefixu: legacy EN cesta
      // na sloučenou destinaci (`/destinations/mauricius1`) by jinak vyrobila
      // dvouskokový 301 přes `/destinace/mauricius1`.
      const czech = to + p.slice(from.length);
      return renamedSlugRedirect(czech) ?? czech;
    }
  }
  return null;
}

/**
 * Cíl `RENAMED_PATH_REDIRECTS` pro danou českou cestu, jinak `null`. Porovnává se nad
 * DEKÓDOVANOU cestou — `url.pathname` chodí percent-encoded. `decodeURIComponent` hodí
 * `URIError` na vadném `%` vstupu (`/destinace/100%`); bez try/catch by middleware vracel
 * 500 na KAŽDÝ takový request, ne jen na ten svůj.
 */
function renamedSlugRedirect(path: string): string | null {
  let decoded: string;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    decoded = path;
  }
  return RENAMED_PATH_REDIRECTS[decoded] ?? null;
}
